/**
 * Desktop quick-add grammar (decision e-6): one deterministic line, no AI.
 *
 *   450 swiggy @rohan #groceries yesterday
 *   +12,000 salary refund 03/09
 *   ₹1,200.50 dmart #household
 *
 * Tokens are whitespace-separated and order-free:
 *   amount     the FIRST token shaped like a number: optional "+" (credit),
 *              optional ₹ / Rs / Rs. prefix, attached or split by spaces
 *              ("₹ 450", "+ ₹ 450", "+ Rs 450" all glue into one amount),
 *              digits with optional grouping commas (1,200 or 1,20,000) and
 *              up to 2 decimals. Must be > 0. Later numbers stay text.
 *   @person    matched to an existing person by name, case-insensitive
 *              (full name with spaces/punctuation dropped, or a unique first
 *              name). The first match wins; an unknown @name stays in the text.
 *   #category  matched by slug or name, case-insensitive ("#food-dining",
 *              "#fooddining", "#groceries"). The first match wins; unknown or
 *              extra #tags stay in the text.
 *   date       "today", "yesterday", or dd/mm (this year; last year if that
 *              would be in the future) or dd/mm/yy(yy) (never after today: bad_date).
 *              The first date wins.
 *              Default: today.
 *   the rest   the description (merchant / what it was for), spaces collapsed.
 *
 * `bare: true` (the phone; typed or dictated text): a bare word that is exactly a person's name (full,
 * or a unique first name) or a category name/slug counts like "@" / "#", so "250 lunch Ayushi" works.
 * Spoken numbers ("two fifty") are NOT parsed: digits only.
 *
 * Errors are codes, so each app words them itself (web: English, phone: i18n).
 */

/** The API caps a plaintext description at this many characters. */
export const MAX_DESCRIPTION_LENGTH = 500

export interface GrammarPerson {
  id: string
  name: string
}
export interface GrammarCategory {
  id: string
  name: string
  slug: string
}

export interface QuickAddParse {
  /** Positive amount, or null when none was typed. */
  amount: number | null
  type: "debit" | "credit"
  description: string
  person: GrammarPerson | null
  category: GrammarCategory | null
  /** "YYYY-MM-DD" (local calendar day). */
  date: string
  /** The date token as typed ("yesterday", "03/09"), null when defaulted to today. */
  dateToken: string | null
  /** What stops a submit. Empty = ready. `token` is the offending text (bad_date). */
  errors: QuickAddError[]
}
export interface QuickAddError {
  code: "no_amount" | "zero_amount" | "no_description" | "bad_date" | "too_long"
  token?: string
}

const AMOUNT = /^(\+)?(?:₹|rs\.?)?((?:\d{1,3}(?:,\d{2,3})+|\d+)(?:\.\d{1,2})?)$/i
/** "+", "₹", "Rs", "Rs.", "+₹", "+Rs." — an amount prefix with no digits yet. */
const PREFIX_ONLY = /^(?:\+(?:₹|rs\.?)?|₹|rs\.?)$/i
const DMY = /^(\d{1,2})\/(\d{1,2})(?:\/(\d{2}|\d{4}))?$/

const norm = (s: string) => s.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, "")

function iso(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0")
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

/** A date token → local Date, "invalid" for a malformed dd/mm, or null if not a date token. */
function parseDate(token: string, today: Date): Date | "invalid" | null {
  const t = token.toLowerCase()
  const base = startOfDay(today)
  if (t === "today") return base
  if (t === "yesterday") return new Date(base.getFullYear(), base.getMonth(), base.getDate() - 1)
  const m = DMY.exec(token)
  if (!m) return null
  const day = Number(m[1])
  const month = Number(m[2])
  let year = m[3] ? Number(m[3]) : base.getFullYear()
  if (m[3] && m[3].length === 2) year += 2000
  const d = new Date(year, month - 1, day)
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day)
    return "invalid"
  // No year typed and it would be in the future → the most recent past one.
  if (!m[3] && d > base) return new Date(year - 1, month - 1, day)
  // A full date after today can't be saved.
  if (d > base) return "invalid"
  return d
}

function matchPerson(name: string, persons: GrammarPerson[]): GrammarPerson | null {
  const want = norm(name)
  if (!want) return null
  const exact = persons.find((p) => norm(p.name) === want)
  if (exact) return exact
  const byFirst = persons.filter((p) => norm(p.name.split(/\s+/)[0] ?? "") === want)
  return byFirst.length === 1 ? (byFirst[0] ?? null) : null
}

function matchCategory(tag: string, categories: GrammarCategory[]): GrammarCategory | null {
  const want = tag.toLowerCase()
  const bySlug = categories.find((c) => c.slug.toLowerCase() === want)
  if (bySlug) return bySlug
  const n = norm(tag)
  if (!n) return null
  return categories.find((c) => norm(c.slug) === n || norm(c.name) === n) ?? null
}

export function parseQuickAdd(
  input: string,
  ctx: {
    persons?: GrammarPerson[]
    categories?: GrammarCategory[]
    today?: Date
    bare?: boolean
  } = {},
): QuickAddParse {
  const persons = ctx.persons ?? []
  const categories = ctx.categories ?? []
  const today = ctx.today ?? new Date()
  const errors: QuickAddError[] = []

  // Glue a split amount back into one token: "₹ 450", "+ 450", "+ ₹ 450",
  // "+ Rs 450", "+₹ 450" → "₹450" / "+450" / "+₹450" / "+Rs450". A lone "+",
  // "₹" or "Rs" only glues onto what follows when the result is still a
  // currency prefix or a whole amount, so "+ tip" stays text.
  const raw = input.trim().split(/\s+/).filter(Boolean)
  const tokens: string[] = []
  for (let i = 0; i < raw.length; i++) {
    let t = raw[i] as string
    while (
      PREFIX_ONLY.test(t) &&
      i + 1 < raw.length &&
      (PREFIX_ONLY.test(t + raw[i + 1]) || AMOUNT.test(t + raw[i + 1]))
    ) {
      t += raw[i + 1]
      i++
    }
    tokens.push(t)
  }

  let amount: number | null = null
  let type: "debit" | "credit" = "debit"
  let person: GrammarPerson | null = null
  let category: GrammarCategory | null = null
  let date: Date | null = null
  let dateToken: string | null = null
  const rest: string[] = []

  for (const token of tokens) {
    if (amount === null) {
      const m = AMOUNT.exec(token)
      if (m) {
        const value = Number((m[2] as string).replace(/,/g, ""))
        if (value > 0) {
          amount = value
          type = m[1] ? "credit" : "debit"
        } else {
          errors.push({ code: "zero_amount" })
        }
        continue
      }
    }
    if (token.startsWith("@") && token.length > 1 && !person) {
      const found = matchPerson(token.slice(1), persons)
      if (found) {
        person = found
        continue
      }
    }
    if (token.startsWith("#") && token.length > 1 && !category) {
      const found = matchCategory(token.slice(1), categories)
      if (found) {
        category = found
        continue
      }
    }
    if (!date) {
      const d = parseDate(token, today)
      if (d === "invalid") {
        errors.push({ code: "bad_date", token })
        continue
      }
      if (d) {
        date = d
        dateToken = token
        continue
      }
    }
    if (ctx.bare) {
      const word = token.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, "")
      const who: GrammarPerson | null = word && !person ? matchPerson(word, persons) : null
      if (who) {
        person = who
        continue
      }
      const cat: GrammarCategory | null = word && !category ? matchCategory(word, categories) : null
      if (cat) {
        category = cat
        continue
      }
    }
    rest.push(token)
  }

  const description = rest.join(" ")
  if (amount === null && !errors.some((e) => e.code === "zero_amount")) {
    errors.unshift({ code: "no_amount" })
  }
  if (!description) errors.push({ code: "no_description" })
  else if (description.length > MAX_DESCRIPTION_LENGTH) errors.push({ code: "too_long" })

  return {
    amount,
    type,
    description,
    person,
    category,
    date: iso(date ?? startOfDay(today)),
    dateToken,
    errors,
  }
}
